import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Gift, Ticket, CheckCircle2, AlertCircle, Loader2, Home } from "lucide-react";

export default function ScanPage() {
  const { businessId } = useParams();
  const [params] = useSearchParams();
  const action = params.get("action") || "points";
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirect = encodeURIComponent(`/escanear/${businessId}?action=${action}`);
      navigate(`/login-cliente?redirect=${redirect}`);
      return;
    }
    if (user.role !== "customer") {
      setError("Esta página es solo para clientes. Inicia sesión con tu cuenta de cliente.");
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("processing");
    const endpoint = action === "raffle" ? "/customer/scan/raffle" : "/customer/scan/points";
    api
      .post(endpoint, { business_id: businessId })
      .then((r) => {
        if (cancelled) return;
        setResult(r.data);
        setStatus("success");
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e.response?.data?.detail || "No pudimos procesar tu escaneo. Intenta de nuevo más tarde.";
        setError(detail);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user, businessId, action, navigate]);

  const isPoints = action === "points";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 grid place-items-center px-4 py-12">
        <div className="max-w-md w-full">
          {status === "loading" && (
            <div className="text-center text-muted-foreground flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
              <p>Cargando…</p>
            </div>
          )}

          {status === "processing" && (
            <div className="text-center text-muted-foreground flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 animate-spin text-[#7C3AED]" />
              <p className="text-lg font-medium">Procesando tu escaneo…</p>
              <p className="text-sm">Estamos registrando tus {isPoints ? "puntos" : "boleto"}.</p>
            </div>
          )}

          {status === "success" && (
            <div className="bg-white border border-border rounded-2xl p-6 text-center space-y-4 shadow-sm">
              <div className={`w-16 h-16 rounded-full mx-auto grid place-items-center ${isPoints ? "bg-[#F0FDF4]" : "bg-[#FEF3F2]"}`}>
                {isPoints ? <Gift className="w-8 h-8 text-[#16A34A]" /> : <Ticket className="w-8 h-8 text-[#E05A47]" />}
              </div>
              <h1 className="font-display text-2xl tracking-tight">
                {isPoints ? "¡Puntos recibidos!" : "¡Boleto recibido!"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {isPoints ? (
                  <>Acabas de recibir <strong>+{result?.points} pts</strong> en <strong>{result?.business_name}</strong>.{result?.raffle_ticket && " También te incluimos un boleto para la rifa mensual."} Tu saldo: <strong>{result?.new_balance} pts</strong>.</>
                ) : (
                  <>Recibiste <strong>1 boleto</strong> para la rifa mensual de <strong>{result?.business_name}</strong>. ¡Suerte!</>
                )}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Link to="/mi-cuenta"><Button className="w-full btn-primary rounded-full">Ver mi cuenta</Button></Link>
                <Link to="/"><Button variant="outline" className="w-full rounded-full">Ir al inicio</Button></Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="bg-white border border-border rounded-2xl p-6 text-center space-y-4 shadow-sm">
              <div className="w-16 h-16 rounded-full mx-auto grid place-items-center bg-rose-50">
                <AlertCircle className="w-8 h-8 text-rose-600" />
              </div>
              <h1 className="font-display text-2xl tracking-tight">No se pudo completar</h1>
              <p className="text-muted-foreground text-sm">{error}</p>
              <div className="flex flex-col gap-2 pt-2">
                <Link to="/mi-cuenta"><Button className="w-full btn-primary rounded-full">Ver mi cuenta</Button></Link>
                <Link to="/"><Button variant="outline" className="w-full rounded-full">Ir al inicio</Button></Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}